import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  WorkflowTopbarBreadcrumbItem,
  WorkflowTopbarConfig,
  WorkflowTopbarStatusTone,
} from '../../models/workflow-topbar.model';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

@Component({
  selector: 'app-workflow-topbar',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './workflow-topbar.component.html',
  styleUrl: './workflow-topbar.component.scss',
})
export class WorkflowTopbarComponent implements AfterViewInit, OnDestroy {
  private static nextInstanceId = 0;
  private static readonly diagnosticKey = '[DVM-WORKFLOW-TOPBAR-DIAG-20260629]';

  private readonly instanceId = ++WorkflowTopbarComponent.nextInstanceId;
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly responsiveLayout = inject(ResponsiveLayoutService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private config: WorkflowTopbarConfig = {};
  private compact = false;
  private cachedBreadcrumbs: WorkflowTopbarBreadcrumbItem[] = [];
  private cachedTopbarStyle: Record<string, string> = {};
  private readonly createdAt = Date.now();
  private reflowWindowStartedAt = Date.now();
  private reflowCount = 0;
  private totalReflowCount = 0;
  private geometryFrameId: number | null = null;

  @Input() set data(value: WorkflowTopbarConfig | null | undefined) {
    this.config = value ? { ...value } : {};
    this.cachedBreadcrumbs = this.resolveBreadcrumbs();
    this.cachedTopbarStyle = this.resolveTopbarStyle();
    const compactChanged = this.updateCompactState();
    this.log('data-set', {
      widgetId: (value as Record<string, unknown> | null | undefined)?.['widgetId'],
      currentLabel: this.currentLabel,
      breadcrumbCount: this.cachedBreadcrumbs.length,
      compact: this.compact,
      compactChanged,
      configuredHeight: (value as Record<string, unknown> | null | undefined)?.['height'],
      topbarHeight: this.config.topbarHeight,
    });
    this.scheduleGeometryLog('data-set');
  }

  get data(): WorkflowTopbarConfig {
    return this.config;
  }

  @Output() readonly backClick = new EventEmitter<void>();
  @Output() readonly breadcrumbClick = new EventEmitter<WorkflowTopbarBreadcrumbItem>();

  @HostBinding('class.is-compact')
  get isCompact(): boolean {
    return this.compact;
  }

  constructor() {
    this.log('constructor', this.viewportSnapshot());
    this.responsiveLayout.init();
    this.responsiveLayout.reflow$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const compactChanged = this.updateCompactState();
        this.recordReflow(compactChanged);
        this.changeDetectorRef.markForCheck();
      });
    this.updateCompactState();
  }

  ngAfterViewInit(): void {
    this.log('after-view-init', this.geometrySnapshot());
  }

  ngOnDestroy(): void {
    if (this.geometryFrameId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.geometryFrameId);
    }
    this.log('destroy', {
      lifetimeMs: Date.now() - this.createdAt,
      totalReflowCount: this.totalReflowCount,
      ...this.geometrySnapshot(),
    });
  }

  get breadcrumbs(): WorkflowTopbarBreadcrumbItem[] {
    return this.cachedBreadcrumbs;
  }

  get currentLabel(): string {
    return String(this.config.currentLabel ?? this.config.workflowName ?? '').trim();
  }

  get versionLabel(): string {
    const version = String(this.config.version ?? '').trim();
    if (!version) return '';

    const prefix = this.config.versionPrefix === undefined ? 'v' : String(this.config.versionPrefix);
    if (!prefix || version.toLowerCase().startsWith(prefix.toLowerCase())) return version;
    return `${prefix}${version}`;
  }

  get statusLabel(): string {
    return String(this.config.status || '').trim();
  }

  get statusTone(): WorkflowTopbarStatusTone {
    if (this.config.statusTone) return this.config.statusTone;

    switch (this.statusLabel.toLowerCase()) {
      case 'draft': return 'draft';
      case 'published': return 'published';
      case 'active': return 'success';
      case 'warning': return 'warning';
      case 'failed':
      case 'error': return 'danger';
      default: return 'neutral';
    }
  }

  get showBack(): boolean {
    return this.config.showBack !== false;
  }

  get topbarStyle(): Record<string, string> {
    return this.cachedTopbarStyle;
  }

  handleBackClick(): void {
    this.log('back-click', {
      backRoute: this.config.backRoute,
      hasCallback: typeof this.config.onBackClick === 'function',
    });
    this.backClick.emit();

    if (this.config.onBackClick) {
      this.config.onBackClick();
      return;
    }

    const route = String(this.config.backRoute || '').trim();
    if (route) {
      void this.router.navigateByUrl(route);
      return;
    }

    this.location.back();
  }

  handleBreadcrumbClick(item: WorkflowTopbarBreadcrumbItem): void {
    this.log('breadcrumb-click', { label: item.label, route: item.route });
    this.breadcrumbClick.emit(item);
    const route = String(item.route || '').trim();
    if (route) void this.router.navigate([route], { queryParams: item.queryParams });
  }

  handleLogoLoad(kind: 'partner', event: Event): void {
    const image = event.target as HTMLImageElement;
    this.log('logo-load', {
      kind,
      src: image.currentSrc || image.src,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
    });
  }

  handleLogoError(kind: 'partner', event: Event): void {
    const image = event.target as HTMLImageElement;
    this.log('logo-error', { kind, src: image.currentSrc || image.src });
  }

  private resolveBreadcrumbs(): WorkflowTopbarBreadcrumbItem[] {
    if (Array.isArray(this.config.breadcrumbs) && this.config.breadcrumbs.length) {
      return this.config.breadcrumbs.filter((item) => !!String(item?.label || '').trim());
    }

    const parentLabel = String(this.config.parentLabel || '').trim();
    return parentLabel ? [{ label: parentLabel, route: this.config.parentRoute }] : [];
  }

  private resolveTopbarStyle(): Record<string, string> {
    return {
      '--workflow-topbar-height': this.normalizeSize(this.config.topbarHeight, '52px'),
      '--workflow-partner-width': this.normalizeSize(this.config.partnerLogo?.width, '92px'),
    };
  }

  private updateCompactState(): boolean {
    if (typeof window === 'undefined') return false;
    const configured = Number(this.config.compactBreakpoint);
    const breakpoint = Number.isFinite(configured) && configured > 0 ? configured : 720;
    const nextCompact = window.innerWidth <= breakpoint;
    const changed = nextCompact !== this.compact;
    this.compact = nextCompact;
    return changed;
  }

  private recordReflow(compactChanged: boolean): void {
    const now = Date.now();
    if (now - this.reflowWindowStartedAt > 5000) {
      this.reflowWindowStartedAt = now;
      this.reflowCount = 0;
    }

    this.reflowCount += 1;
    this.totalReflowCount += 1;
    const payload = {
      windowCount: this.reflowCount,
      totalCount: this.totalReflowCount,
      compact: this.compact,
      compactChanged,
      ...this.viewportSnapshot(),
    };

    if (this.reflowCount === 20) {
      console.warn(WorkflowTopbarComponent.diagnosticKey, `#${this.instanceId}`, 'reflow-storm', payload);
    } else if (this.totalReflowCount <= 10 || this.totalReflowCount % 25 === 0 || compactChanged) {
      this.log('responsive-reflow', payload);
    }

    this.scheduleGeometryLog('responsive-reflow');
  }

  private scheduleGeometryLog(reason: string): void {
    if (typeof requestAnimationFrame !== 'function') return;
    if (this.geometryFrameId !== null) cancelAnimationFrame(this.geometryFrameId);
    this.geometryFrameId = requestAnimationFrame(() => {
      this.geometryFrameId = null;
      this.log('geometry', { reason, ...this.geometrySnapshot() });
    });
  }

  private geometrySnapshot(): Record<string, unknown> {
    const host = this.elementRef.nativeElement;
    const rect = host.getBoundingClientRect();
    return {
      connected: host.isConnected,
      width: Math.round(rect.width),
      height: Math.round(rect.height),
      top: Math.round(rect.top),
      left: Math.round(rect.left),
    };
  }

  private viewportSnapshot(): Record<string, unknown> {
    return typeof window === 'undefined'
      ? { viewport: 'unavailable' }
      : { viewportWidth: window.innerWidth, viewportHeight: window.innerHeight };
  }

  private log(event: string, payload?: unknown): void {
    console.info(WorkflowTopbarComponent.diagnosticKey, `#${this.instanceId}`, event, payload ?? '');
  }

  private normalizeSize(value: string | number | undefined, fallback: string): string {
    if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`;
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }
}
